import { falCat } from 'shared/@generated/icons/falCat';
import { falDog } from 'shared/@generated/icons/falDog';
import { falMars } from 'shared/@generated/icons/falMars';
import { falPaw } from 'shared/@generated/icons/falPaw';
import { falVenus } from 'shared/@generated/icons/falVenus';
import { useRef, useState } from 'react';
import { Animated, Easing, Platform, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Props } from 'shared/@types';
import { Text } from 'shared/components/text';
import { H1, H2 } from 'shared/components/title';
import { Link } from 'shared/components/link';
import { MarkdownView } from 'shared/components/markdown-view';
import { FaIcon, Icon } from 'shared/components/icon';
import { Button } from 'shared/components/button';
import { useRoute } from 'shared/library/routes/route';
import { translateArray } from 'shared/library/i18n/get-translation-list';
import colors from 'shared/library/colors';
import { dogNameStats, PetNameAnimal, PetNameGender, petNames } from 'shared/library/pet-names';

interface PetNamesScreenProps extends Props {
  animal: PetNameAnimal;
  gender?: PetNameGender;
}

const ROW_HEIGHT = 76;
const SPIN_STEPS = 18;
const DOG_REGISTER_URL =
  'https://jordbruksverket.se/download/18.715de5b19b9142b1b3f08a/1767944570204/De-popularaste-hundnamnen-2025-tga.pdf';
const POINTER_HEIGHT = 44;
const POINTER_WIDTH = 26;
const pointerStyle = {
  top: ROW_HEIGHT * 1.5 - POINTER_HEIGHT / 2,
  width: 0,
  height: 0,
  borderTopWidth: POINTER_HEIGHT / 2,
  borderBottomWidth: POINTER_HEIGHT / 2,
  borderTopColor: 'transparent',
  borderBottomColor: 'transparent',
  borderLeftColor: colors.brand['brown-dark'],
  borderRightColor: colors.brand['brown-dark'],
};
const genders: PetNameGender[] = ['female', 'male'];
const animals: PetNameAnimal[] = ['dog', 'cat'];
const chipColors = [
  'bg-orange-500',
  'bg-brand-pink',
  'bg-brand-yellow',
  'bg-brand-sky',
  'bg-brand-lilac',
  'bg-brand-green-semilight',
];
const stickerStyles = [
  'bg-orange-500 -rotate-6 self-end',
  'bg-brand-pink rotate-3 self-start',
  'bg-brand-yellow -rotate-3 self-center',
  'bg-brand-lilac rotate-6 self-start',
  'bg-brand-green-semilight -rotate-3 self-end',
  'bg-brand-sky rotate-3 self-center',
];

function pageKey(animal: PetNameAnimal, gender?: PetNameGender) {
  return gender ? (`${animal}_${gender}` as const) : animal;
}

function routeName(animal: PetNameAnimal, gender?: PetNameGender) {
  return (gender ? `pet-names.${animal}.${gender}` : `pet-names.${animal}`) as 'pet-names.dog';
}

function chipColor(name: string) {
  const hash = Array.from(name).reduce((sum, char) => sum + char.charCodeAt(0), 0);

  return chipColors[hash % chipColors.length];
}

function pickName(pool: string[], exclude?: string) {
  const options = pool.filter((name) => name !== exclude);

  return options[Math.floor(Math.random() * options.length)]!;
}

const PetNamesScreen = ({ animal, gender }: PetNamesScreenProps) => {
  const { t, i18n } = useTranslation('screens');
  const route = useRoute();
  const page = pageKey(animal, gender);
  const listGenders = gender ? [gender] : genders;
  const pool = listGenders.flatMap((g) => petNames[animal][g]);
  const examples = listGenders.flatMap((g) => petNames[animal][g].slice(2, gender ? 8 : 5));

  const [strip, setStrip] = useState(() => [pool[pool.length - 1]!, pool[0]!, pool[1]!]);
  const [spinning, setSpinning] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const offset = useRef(new Animated.Value(0)).current;

  function spin() {
    if (spinning) {
      return;
    }

    const currentName = strip[strip.length - 2]!;
    const target = pickName(pool, currentName);
    const fillers = Array.from({ length: SPIN_STEPS }, () => pickName(pool));
    const nextStrip = [strip[strip.length - 3]!, currentName, ...fillers, target, pickName(pool)];

    setSpinning(true);
    setStrip(nextStrip);
    offset.setValue(0);

    Animated.timing(offset, {
      toValue: -(nextStrip.length - 3) * ROW_HEIGHT,
      duration: 1600,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start(() => {
      setSpinning(false);
      setHistory((current) => [target, ...current.filter((n) => n !== target)].slice(0, 12));
    });
  }

  const sections = translateArray<{ title: string; text: string }>(
    t,
    `pet_names.article.${animal}.sections`,
  );
  const sources = translateArray<{ title: string; url: string }>(
    t,
    `pet_names.article.${animal}.sources`,
  );
  const articleMarkdown = [
    `## ${t(`pet_names.article.${animal}.title`)}`,
    t(`pet_names.article.${animal}.intro`),
    ...sections.map((section) => `### ${section.title}\n\n${section.text}`),
    `### ${t('pet_names.more_names')}`,
    animals
      .flatMap((a) => [undefined, ...genders].map((g) => [a, g] as const))
      .filter(([a, g]) => a !== animal || g !== gender)
      .map(
        ([a, g]) =>
          `- [${t(`pet_names.pages.${pageKey(a, g)}.link_title`)}](${route(routeName(a, g))}): ${t(`pet_names.pages.${pageKey(a, g)}.link_description`)}`,
      )
      .join('\n'),
    `### ${t('pet_names.sources')}`,
    sources.map((source) => `- [${source.title}](${source.url})`).join('\n'),
  ].join('\n\n');
  const frameColor = animal === 'dog' ? 'bg-orange-500' : 'bg-brand-lilac';

  return (
    <View className={'px-3 py-6 md:px-6 md:py-12'}>
      <View className={'mx-auto w-full max-w-screen-xl'}>
        <View className={'md:items-center'}>
          <H1
            title={t(`pet_names.pages.${page}.heading`)}
            className={'md:text-center'}
            marginBottom={false}
          />
          <Text
            className={
              'text-brand-brown-dark mt-1.5 max-w-prose text-pretty md:mt-3 md:text-center md:text-lg'
            }
          >
            {t(`pet_names.pages.${page}.intro`)}
          </Text>

          <View className={'mt-6 flex-row flex-wrap gap-3 md:justify-center'}>
            <SegmentedLinks
              items={animals.map((a) => ({
                key: a,
                label: t(`pet_names.animal.${a}`),
                icon: a === 'dog' ? falDog : falCat,
                href: route(routeName(a, gender)),
                active: a === animal,
              }))}
            />

            <SegmentedLinks
              items={[undefined, ...genders].map((g) => ({
                key: g ?? 'all',
                label: g ? t(`pet_names.gender.${animal}_${g}`) : t('pet_names.gender.all'),
                icon: g === 'female' ? falVenus : g === 'male' ? falMars : falPaw,
                href: route(routeName(animal, g)),
                active: g === gender,
              }))}
            />
          </View>
        </View>

        <View className={'mt-4 w-full flex-row items-center justify-center gap-12 md:mt-6'}>
          <ExampleStickers names={examples.slice(0, 3)} styles={stickerStyles.slice(0, 3)} />

          <View className={'w-full max-w-lg pt-6 md:pt-8'}>
            <View className={'relative'}>
              <View
                className={clsx(
                  'border-brand-brown-dark rounded-3xl border-2 p-3 md:p-4',
                  frameColor,
                )}
              >
                <View
                  className={
                    'border-brand-brown-dark mb-3 self-center rounded-full border-2 bg-white px-5 py-1.5'
                  }
                >
                  <Text className={'text-brand-brown-dark font-bold'}>
                    {t(`pet_names.machine_label.${animal}`)}
                  </Text>
                </View>

                <View
                  className={
                    'border-brand-brown-dark relative overflow-hidden rounded-2xl border-2 bg-white'
                  }
                  style={{ height: ROW_HEIGHT * 3 + 4 }}
                >
                  <Animated.View style={{ transform: [{ translateY: offset }] }}>
                    {strip.map((name, index) => (
                      <View
                        key={index}
                        className={'items-center justify-center px-6'}
                        style={{ height: ROW_HEIGHT }}
                      >
                        <Text
                          numberOfLines={1}
                          className={
                            'text-brand-brown-dark text-4xl font-black leading-tight md:text-5xl md:leading-tight'
                          }
                        >
                          {name}
                        </Text>
                      </View>
                    ))}
                  </Animated.View>

                  <View
                    pointerEvents={'none'}
                    className={'absolute inset-x-0 top-0 bg-white/75'}
                    style={{ height: ROW_HEIGHT }}
                  />
                  <View
                    pointerEvents={'none'}
                    className={'border-brand-brown-dark/10 absolute inset-x-0 border-y-2'}
                    style={{ top: ROW_HEIGHT, height: ROW_HEIGHT }}
                  />
                  <View
                    pointerEvents={'none'}
                    className={'absolute inset-x-0 bottom-0 bg-white/75'}
                    style={{ height: ROW_HEIGHT }}
                  />
                  <View
                    pointerEvents={'none'}
                    className={'absolute left-0'}
                    style={[pointerStyle, { borderLeftWidth: POINTER_WIDTH }]}
                  />
                  <View
                    pointerEvents={'none'}
                    className={'absolute right-0'}
                    style={[pointerStyle, { borderRightWidth: POINTER_WIDTH }]}
                  />
                </View>
              </View>
            </View>

            <Button
              text={t('pet_names.spin')}
              onPress={spin}
              center={true}
              accessibilityLabel={t('pet_names.spin')}
              className={'mt-5 rounded-2xl py-4'}
              textClassName={'text-2xl md:text-2xl'}
            />
          </View>

          <ExampleStickers names={examples.slice(3, 6)} styles={stickerStyles.slice(3, 6)} />
        </View>

        <View className={'mt-10 items-center md:mt-12'}>
          <Text className={'text-brand-brown-dark text-xl font-black'}>
            {t('pet_names.history')}
          </Text>

          {history.length > 0 ? (
            <View className={'mt-3 max-w-3xl flex-row flex-wrap justify-center gap-2'}>
              {history.map((name) => (
                <View key={name} className={clsx('rounded-xl px-4 py-2', chipColor(name))}>
                  <Text className={'text-brand-brown-dark text-lg font-bold'}>{name}</Text>
                </View>
              ))}
            </View>
          ) : (
            <View aria-hidden={true} className={'mt-3 flex-row justify-center gap-2'}>
              {[1, 2, 3].map((slot) => (
                <View
                  key={slot}
                  className={
                    'border-brand-brown-dark/25 bg-brand-brown-dark/5 w-28 items-center rounded-xl border-2 border-dashed py-2'
                  }
                >
                  <Text className={'text-brand-brown-dark/40 text-lg font-bold'}>
                    {t('pet_names.history_placeholder')}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {listGenders.map((g) => (
          <View key={g} className={'mx-auto w-full max-w-5xl'}>
            <H2 title={t(`pet_names.lists.${animal}_${g}`)} center={true} />

            {animal === 'dog' ? (
              <NameTable
                columns={2}
                headers={[
                  t('pet_names.table.rank'),
                  t('pet_names.table.name'),
                  t('pet_names.table.count'),
                ]}
                rows={dogNameStats[g].map((stat) => [
                  String(stat.rank),
                  stat.name,
                  stat.count.toLocaleString(i18n.language),
                ])}
              />
            ) : (
              <NameTable
                columns={3}
                headers={[t('pet_names.table.name')]}
                rows={[...petNames.cat[g]]
                  .sort((a, b) => a.localeCompare(b, i18n.language))
                  .map((name) => [name])}
              />
            )}

            {animal === 'dog' && (
              <Link
                href={DOG_REGISTER_URL}
                openInNewTab={true}
                containerClassName={'mt-3 self-center'}
                textClassName={'text-brand-brown-dark/60 text-sm underline'}
              >
                {t('pet_names.table.source')}
              </Link>
            )}
          </View>
        ))}

        <MarkdownView className={'mt-12 md:mt-16'} markdown={articleMarkdown} />
      </View>
    </View>
  );
};

function NameTable({
  headers,
  rows,
  columns,
}: {
  headers: string[];
  rows: string[][];
  columns: number;
}) {
  const perColumn = Math.ceil(rows.length / columns);
  const chunks = Array.from({ length: columns }, (_, index) =>
    rows.slice(index * perColumn, (index + 1) * perColumn),
  );

  // Same table styling as the breed characteristics and comparison tables.
  return (
    <View className={'gap-4 md:flex-row md:items-start'}>
      {chunks.map((chunk, index) => (
        <View
          key={index}
          role={'table'}
          className={
            'border-brand-brown-dark/15 overflow-hidden rounded-lg border bg-white md:flex-1'
          }
        >
          <View
            role={'row'}
            className={
              'bg-brand-brown-dark/5 border-brand-brown-dark/15 flex-row items-center border-b p-3'
            }
          >
            {headers.map((header, cell) => (
              <Text
                key={header}
                role={'columnheader'}
                className={clsx(
                  'text-brand-brown-dark text-sm font-bold md:text-base',
                  tableCellClass(cell, headers.length),
                )}
              >
                {header}
              </Text>
            ))}
          </View>

          {chunk.map((row) => (
            <View
              key={row.join('-')}
              role={'row'}
              className={
                'border-brand-brown-dark/15 flex-row items-center border-b p-3 last:border-b-0'
              }
            >
              {row.map((value, cell) => (
                <Text
                  key={cell}
                  role={'cell'}
                  className={clsx(
                    'text-brand-brown-dark text-sm md:text-base',
                    tableCellClass(cell, row.length),
                    row.length > 1 && cell !== 1 && 'tabular-nums',
                  )}
                >
                  {value}
                </Text>
              ))}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

function tableCellClass(cell: number, length: number) {
  if (length === 1) {
    return 'flex-1';
  }

  return cell === 0 ? 'w-14' : cell === length - 1 ? 'w-28 text-right' : 'flex-1';
}

function ExampleStickers({ names, styles }: { names: string[]; styles: string[] }) {
  return (
    <View aria-hidden={true} className={'hidden w-52 gap-6 lg:flex'}>
      {names.map((name, index) => (
        <View
          key={name}
          className={clsx('border-brand-brown-dark rounded-2xl border-2 px-6 py-3', styles[index])}
        >
          <Text className={'text-brand-brown-dark text-2xl font-black'}>{name}</Text>
        </View>
      ))}
    </View>
  );
}

function SegmentedLinks({
  items,
}: {
  items: Array<{ key: string; label: string; icon: FaIcon; href: string; active: boolean }>;
}) {
  return (
    <View className={'border-brand-brown-dark/15 flex-row rounded-full border bg-white p-1'}>
      {items.map((item) => (
        <Link key={item.key} href={item.href} asBlock={true} skipDefaults={true}>
          <View
            className={clsx('flex-row items-center gap-2 rounded-full px-4 py-2', {
              'bg-brand-brown-dark': item.active,
              'hover:bg-brand-cream': !item.active,
            })}
          >
            <Icon
              icon={item.icon}
              className={clsx('size-4', {
                'text-white': item.active,
                'text-brand-brown-dark': !item.active,
              })}
            />
            <Text
              className={clsx({
                'font-bold text-white': item.active,
                'text-brand-brown-dark': !item.active,
              })}
            >
              {item.label}
            </Text>
          </View>
        </Link>
      ))}
    </View>
  );
}

export { PetNamesScreen };
export type { PetNamesScreenProps };
